import _ from "lodash";
import { LoadedEventDefinition } from "./types/global";

export type EventLibrary =
  | "web"
  | "react-native"
  | "sql"
  | "analytics-android"
  | "analytics-ios"
  | "call-rail"
  | "clearbit"
  | "customer-io"
  | "intercom"
  | "invoca"
  | "kustomer"
  | "mailchimp"
  | "sendgrid"
  | "sendinblue"
  | "server";

export type EventType =
  // web
  | "change"
  | "click"
  | "network"
  | "pageview"
  | "submit"
  | "manual_tracked"

  // react-native
  | "text_edit"
  | "touch"
  | "toggle"
  | "react_navigation_screenview"
  | "rn_custom"

  // sql
  | "scheduled_event"
  | "scheduled_identify"

  // other
  | "analytics-android"
  | "analytics-ios"
  | "batch_event"
  | "call-rail"
  | "clearbit"
  | "customer-io"
  | "intercom"
  | "invoca"
  | "kustomer"
  | "mailchimp"
  | "sendgrid"
  | "sendinblue"
  | "server";

export type EventSource =
  | "autotrack"
  | "manual_tracked"
  | "react-native"
  | "sql"
  | "analytics-android"
  | "analytics-ios"
  | "call-rail"
  | "clearbit"
  | "customer-io"
  | "intercom"
  | "invoca"
  | "kustomer"
  | "mailchimp"
  | "sendgrid"
  | "sendinblue"
  | "server";

export function isCustomEventType(eventType: EventType): boolean {
  return (
    eventType === "manual_tracked" ||
    eventType === "rn_custom" ||
    eventType === "analytics-android" ||
    eventType === "analytics-ios" ||
    eventType === "call-rail" ||
    eventType === "clearbit" ||
    eventType === "customer-io" ||
    eventType === "intercom" ||
    eventType === "invoca" ||
    eventType === "kustomer" ||
    eventType === "mailchimp" ||
    eventType === "sendgrid" ||
    eventType === "sendinblue" ||
    eventType === "server"
  );
}

const webEvents: EventType[] = [
  "pageview",
  "click",
  "submit",
  "change",
  "manual_tracked",
  "network",
];
const reactNativeEvents: EventType[] = [
  "touch",
  "text_edit",
  "toggle",
  "react_navigation_screenview",
  "rn_custom",
];
const sqlEvents: EventType[] = ["scheduled_event", "scheduled_identify"];
const androidEvents: EventType[] = ["analytics-android"];
const iosEvents: EventType[] = ["analytics-ios"];
const callrailEvents: EventType[] = ["call-rail"];
const clearbitEvents: EventType[] = ["clearbit"];
const customerIOEvents: EventType[] = ["customer-io"];
const intercomEvents: EventType[] = ["intercom"];
const invocaEvents: EventType[] = ["invoca"];
const kustomerEvents: EventType[] = ["kustomer"];
const mailchimpEvents: EventType[] = ["mailchimp"];
const sendGridEvents: EventType[] = ["sendgrid"];
const sendInBlueEvents: EventType[] = ["sendinblue"];
const serverEvents: EventType[] = ["server"];

// arrayToMap converts the given array to a map where each element of the array
// is a key in the map. The passed in value will be used as the value for all
// keys.
function arrayToMap(arr: string[], val: string): Record<string, string> {
  const result = {};

  for (const elem of arr) {
    result[elem] = val;
  }

  return result;
}

const eventTypeToLibraryMap: Record<EventType, EventLibrary> = _.extend(
  {},
  arrayToMap(webEvents, "web"),
  arrayToMap(reactNativeEvents, "react-native"),
  arrayToMap(sqlEvents, "sql"),
  arrayToMap(androidEvents, "analytics-android"),
  arrayToMap(callrailEvents, "call-rail"),
  arrayToMap(clearbitEvents, "clearbit"),
  arrayToMap(customerIOEvents, "customer-io"),
  arrayToMap(intercomEvents, "intercom"),
  arrayToMap(invocaEvents, "invoca"),
  arrayToMap(iosEvents, "analytics-ios"),
  arrayToMap(kustomerEvents, "kustomer"),
  arrayToMap(mailchimpEvents, "mailchimp"),
  arrayToMap(sendGridEvents, "sendgrid"),
  arrayToMap(sendInBlueEvents, "sendinblue"),
  arrayToMap(serverEvents, "server")
);

export const allEventTypes: EventType[] = _.concat(
  webEvents,
  reactNativeEvents,
  sqlEvents,
  androidEvents,
  callrailEvents,
  clearbitEvents,
  customerIOEvents,
  intercomEvents,
  invocaEvents,
  iosEvents,
  kustomerEvents,
  mailchimpEvents,
  sendGridEvents,
  sendInBlueEvents,
  serverEvents
);

export function isServerSideSource(eventSource: EventSource) {
  return eventSource !== "autotrack" && eventSource !== "manual_tracked";
}

// libraryForEventType returns the library that sends a specific event type.
export function libraryForEventType(
  eventType?: EventType | null
): EventLibrary | "unknown" {
  if (eventType === undefined || eventType === null) {
    return "unknown";
  }

  return eventTypeToLibraryMap[eventType] || "unknown";
}

// eventSourceForEventType returns where the event will come from for the given
// event type.
export function eventSourceForEventType(eventType: EventType) {
  if (_.includes(reactNativeEvents, eventType)) {
    return "react-native";
  } else if (_.includes(sqlEvents, eventType)) {
    return "sql";
  } else {
    return "autotrack";
  }
}

export function eventDefType(eventDef: LoadedEventDefinition): EventType {
  // We can determine if an event is a specific kind of autotracked events based
  // on the $event_type filter.
  const eventTypeFilter = eventDef.filter.filters.find(
    (filter) => filter.prop === "$event_type"
  );
  if (eventTypeFilter) {
    return eventTypeFilter.val as EventType;
  }

  // We can determine if an event is a server side or custom event based on the mplib filter.
  const lib = eventDef.filter.filters.find((filter) => filter.prop === "mp_lib")
    ?.val;
  if (
    lib === "server" ||
    lib === "analytics-android" ||
    lib === "analytics-ios" ||
    lib === "call-rail" ||
    lib === "clearbit" ||
    lib === "customer-io" ||
    lib === "intercom" ||
    lib === "invoca" ||
    lib === "kustomer" ||
    lib === "mailchimp" ||
    lib === "sendgrid" ||
    lib === "sendinblue"
  ) {
    return lib;
  } else if (lib === "sql") {
    return eventDef.type ?? "scheduled_event";
  } else if (lib === "react-native") {
    return "rn_custom";
  } else {
    return "manual_tracked";
  }
}

export const eventCategories = [
  { name: "Pageviews", type: "pageview" },
  { name: "Clicks", type: "click" },
  { name: "Submit", type: "submit" },
  { name: "Change", type: "change" },
  { name: "Network", type: "network" },
  { name: "Precision", type: "manual_tracked" },
  { name: "Server", type: "server" },
  { name: "SQL Events", type: "scheduled_event" },
  { name: "SQL Identify", type: "scheduled_identify" },
  { name: "Android", type: "analytics-android" },
  { name: "iOS", type: "analytics-ios" },

  { name: "[React Native] Touch", type: "touch" },
  { name: "[React Native] Edit Text", type: "text_edit" },
  { name: "[React Native] Toggle Switch", type: "toggle" },
  { name: "[React Native] Screen View", type: "react_navigation_screenview" },
  { name: "[React Native] Precision", type: "rn_custom" },

  { name: "Sendinblue", type: "sendinblue" },
  { name: "Mailchimp", type: "mailchimp" },
  { name: "Intercom", type: "intercom" },
  { name: "SendGrid", type: "sendgrid" },
  { name: "Customer.io", type: "customer-io" },
  { name: "Kustomer", type: "kustomer" },
  { name: "Invoca", type: "invoca" },
  { name: "Clearbit", type: "clearbit" },
  { name: "CallRail", type: "call-rail" },
];

const eventCategoriesMap: Record<string, string> = {};
for (let cat of eventCategories) {
  eventCategoriesMap[cat.type] = cat.name;
}

export { eventCategoriesMap };
